import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  Filter,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Download
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const EstimateCard = ({ estimate, clientName, onClick, t }) => {
  const statusConfig = {
    draft: { icon: Clock, color: 'bg-gray-100 text-gray-700', label: t('draft') },
    sent: { icon: Send, color: 'bg-amber-100 text-amber-700', label: t('sent') },
    approved: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: t('approved') },
    declined: { icon: XCircle, color: 'bg-red-100 text-red-700', label: t('declined') },
    expired: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: t('expired') }
  };
  
  const status = statusConfig[estimate.status] || statusConfig.draft;
  const StatusIcon = status.icon;
  const isRevision = !!estimate.parent_estimate_id;
  
  return (
    <button
      onClick={onClick}
      className="card-default w-full text-left group"
      data-testid={`estimate-card-${estimate.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status.color}`}>
            <StatusIcon className="w-5 h-5" />
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${status.color}`}>
              {status.label}
            </span>
            {isRevision && (
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                Revision
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
      
      <h3 className="font-heading text-lg font-semibold mb-1 group-hover:text-primary transition-colors">
        {estimate.project_title || estimate.service_type}
      </h3>
      <p className="text-muted-foreground">{clientName}</p>
      
      {estimate.job_address && (
        <p className="text-sm text-muted-foreground mt-1 truncate">{estimate.job_address}</p>
      )}
      
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
        <span className="text-sm text-muted-foreground">
          {formatDistanceToNow(new Date(estimate.created_at), { addSuffix: true })}
        </span>
        <span className="font-heading text-xl font-bold">
          ${estimate.total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>
    </button>
  );
};

const EstimatesPage = () => {
  const { organization } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [estimates, setEstimates] = useState([]);
  const [clients, setClients] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadEstimates();
  }, [organization?.id]);

  const loadEstimates = async () => {
    if (!organization?.id) return;
    
    try {
      const [estimatesRes, clientsRes] = await Promise.all([
        apiHelpers.getEstimates(organization.id),
        apiHelpers.getClients(organization.id)
      ]);
      
      setEstimates(estimatesRes.data);
      
      // Create client lookup map
      const clientMap = {};
      clientsRes.data.forEach(client => {
        clientMap[client.id] = client.name;
      });
      setClients(clientMap);
    } catch (error) {
      console.error('Failed to load estimates:', error);
      toast.error(t('failed_to_load'));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredEstimates = estimates.filter(est => {
    if (filter === 'all') return true;
    return est.status === filter;
  });

  const filterOptions = [
    { value: 'all', label: t('all') },
    { value: 'draft', label: t('draft') },
    { value: 'sent', label: t('sent') },
    { value: 'approved', label: t('approved') },
    { value: 'declined', label: t('declined') }
  ];

  const handleExportCSV = async () => {
    try {
      const response = await apiHelpers.exportEstimates(organization.id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `estimates-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Estimates exported to CSV');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export estimates');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="estimates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground tracking-tight">
            {t('estimates_title')}
          </h1>
          <p className="text-muted-foreground text-base md:text-lg mt-1">
            {estimates.length} {t('total_estimates')}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            data-testid="export-estimates-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => navigate('/estimates/new')}
            className="btn-secondary"
            data-testid="new-estimate-btn"
          >
            <Plus className="w-5 h-5 mr-2" strokeWidth={2.5} />
            {t('new_estimate')}
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {filterOptions.map((option) => (
          <Button
            key={option.value}
            variant={filter === option.value ? "default" : "outline"}
            onClick={() => setFilter(option.value)}
            className={`h-10 px-4 rounded-full ${
              filter === option.value ? 'bg-primary text-white' : ''
            }`}
            data-testid={`filter-${option.value}`}
          >
            {option.label}
            {option.value !== 'all' && (
              <span className="ml-2 opacity-70">
                ({estimates.filter(e => e.status === option.value).length})
              </span>
            )}
          </Button>
        ))}
      </div>
      
      {/* Estimates Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredEstimates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEstimates.map((estimate) => (
            <EstimateCard
              key={estimate.id}
              estimate={estimate}
              clientName={clients[estimate.client_id] || t('customers').slice(0, -1)}
              onClick={() => navigate(`/estimates/${estimate.id}`)}
              t={t}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 card-default">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="font-heading text-xl font-semibold mb-2">
            {filter !== 'all' ? `${t('no_results')}` : t('no_results')}
          </h3>
          <p className="text-muted-foreground mb-6">
            {filter !== 'all' 
              ? t('try_different_search') 
              : t('try_different_search')}
          </p>
          {filter === 'all' && (
            <Button
              onClick={() => navigate('/estimates/new')}
              className="btn-primary"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('create_estimate')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EstimatesPage;
